import { cloneElement, useCallback, useState } from "react";

import { AmplitudeEvents } from "../../../common/constants/amplitude";
import { useAmplitude } from "../../../common/hooks/amplitude/useAmplitude";
import { DashboardDialog } from "../Dialog/DashboardDialog";
import { ActionProps, DialogProps, AdditionalActionProps } from "../Dialog/types";

interface ActionModalProps extends Omit<DialogProps, "actions" | "onClose" | "onOpen" | "open" | "content"> {
  content: React.ReactNode;
  defaultIsOpenValue?: boolean;
  children: React.ReactElement<{ onClick?: () => void }>;
  actions: {
    main: Omit<ActionProps, "onClick"> & { onClick?: () => void };
    cancel?: Omit<ActionProps, "onClick"> & { onClick?: () => void };
    additional?: AdditionalActionProps[];
    onOpen?: Omit<ActionProps, "onClick"> & { onClick?: () => void };
  };
  amplitudeEventOnOpen?: AmplitudeEvents;
  disabled?: boolean;
}

export const DashboardDialogValidation = ({
  content,
  actions,
  children,
  defaultIsOpenValue,
  title,
  amplitudeEventOnOpen,
  disabled,
  ...rest
}: ActionModalProps) => {
  const { logAmplitudeEvent } = useAmplitude();
  const [isModalOpen, setIsModalOpen] = useState(defaultIsOpenValue || false);

  const openModal = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsModalOpen(true);
    actions.onOpen?.onClick?.();
  }, [actions.onOpen, disabled]);

  const closeModal = useCallback(() => {
    actions.cancel?.onClick?.();
    setIsModalOpen(false);
  }, [actions.cancel]);

  const onValidate = useCallback(async () => {
    await actions.main?.onClick?.();
    setIsModalOpen(false);
  }, [actions.main]);

  return (
    <>
      <DashboardDialog
        open={isModalOpen}
        onClose={closeModal}
        title={title}
        actions={{
          ...actions,
          main: { ...actions.main, onClick: onValidate },
          cancel: { ...actions.cancel, onClick: closeModal },
        }}
        {...rest}
      >
        {content}
      </DashboardDialog>
      {children &&
        cloneElement(children, {
          onClick: () => {
            amplitudeEventOnOpen && logAmplitudeEvent(amplitudeEventOnOpen);
            openModal();
          },
        })}
    </>
  );
};
